// components/OrganisationMenu.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Image as ImageIcon, PackagePlus, Upload } from '@/components/ui/lucide-memo';
import SimpleLoader from './ui/SimpleLoader';
import AddMemberModal from './AddMemberModal';
import PendingInvitesList from './PendingInvitesList';
import { toast } from 'sonner';
import CreateCustomPlanModal, { PlanDetails as CreatePlanDetails } from './CreateCustomPlanModal';
import axios, { AxiosError } from 'axios';
import { format } from 'date-fns';
import AgentPlaybookModal from './AgentPlaybookModal';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { useUser } from '@clerk/nextjs';
import { CollaborationMode, MemberData, PendingInvite } from '#types/types';
import { formatCreditsInteger } from '#lib/formatCredits';
import OrganisationConsumptionTimeline from './OrganisationConsumptionTimeline';
import { TeamManagementSection } from './organisation/TeamManagementSection';
import InvoicesSection from './organisation/InvoicesSection';
import { useOrganisationMemberActions } from './organisation/useOrganisationMemberActions';

interface OrganisationMenuProps {
  isActive: boolean;
  targetOrgId?: string | null;
  settingsView?: 'all' | 'general' | 'people' | 'usage' | 'billing';
}

// ChatGPT's settings controls (owner, 2026-09-29, chatgpt.com/settings): a value you pick is a
// borderless pill that greys on hover; an action is a filled #212121 pill. 15px everywhere, pinned
// inline by CONTROL_TEXT because the global clamp shrinks button text to 12px.
const selectClass = 'h-9 cursor-pointer rounded-full bg-transparent px-3 text-[#ECECEC] outline-none transition-colors hover:bg-[#212121] focus-visible:bg-[#212121] disabled:cursor-not-allowed disabled:opacity-45';
const secondaryButtonClass = 'inline-flex h-9 items-center justify-center gap-2 rounded-full bg-[#212121] px-4 text-[#ECECEC] transition-colors hover:bg-[#2F2F2F] disabled:cursor-not-allowed disabled:opacity-45';
const primaryButtonClass = 'inline-flex h-9 items-center justify-center gap-2 rounded-full bg-[#F0F0F0] px-4 font-medium text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40';
const CONTROL_TEXT: React.CSSProperties = { fontSize: 15 };

const OrganisationSection = React.memo(({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="mb-10">
    <div className="flex min-h-9 items-center justify-between gap-4 pb-2">
      <h2 className="font-medium text-[#ECECEC]" style={{ fontSize: 17, lineHeight: '24px' }}>{title}</h2>
      {action}
    </div>
    <div className="divide-y divide-white/[0.08]">{children}</div>
  </section>
));
OrganisationSection.displayName = 'OrganisationSection';

const OrganisationSettingRow = React.memo(({
  label,
  description,
  control,
  align = 'center',
}: {
  label: string;
  description?: React.ReactNode;
  control: React.ReactNode;
  align?: 'center' | 'start';
}) => (
  <div className={`flex min-h-[60px] flex-col gap-2.5 py-3 sm:flex-row sm:gap-6 ${align === 'start' ? 'sm:items-start' : 'sm:items-center'}`}>
    <div className="min-w-0 flex-1">
      <div className="text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '21px' }}>{label}</div>
      {description ? <div className="mt-1 max-w-[560px] text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>{description}</div> : null}
    </div>
    <div className="flex min-h-9 shrink-0 items-center sm:justify-end">{control}</div>
  </div>
));
OrganisationSettingRow.displayName = 'OrganisationSettingRow';

const OrganisationMenu: React.FC<OrganisationMenuProps> = ({ isActive, targetOrgId = null, settingsView = 'all' }) => {
  const { kernel } = useKernel();
  const userContext = useKernelStore(state => state.userContext);
  const { user } = useUser();
  // --- State ---
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const orgRegistry = useKernelStore(state => state.inventory.Organisation.registry);
  const userRegistry = useKernelStore(state => state.inventory.User.registry);
  
  const currentOrgId = targetOrgId || userContext.organisationId;
  const orgDetails = currentOrgId ? orgRegistry[currentOrgId] : null;
  const shouldLoadPendingInvites = settingsView === 'all' || settingsView === 'people';

  const activePlans = useMemo(() => orgDetails?.plans || [], [orgDetails?.plans]);
  const stats = orgDetails?.stats || null;
  
  const members = useMemo(() => {
    if (!currentOrgId) return [];
    return Object.values(userRegistry).filter(u => u.organisationId === currentOrgId) as MemberData[];
  }, [userRegistry, currentOrgId]);
  const [logoExists, setLogoExists] = useState(true);
  const isOrganisationAdmin = userContext.isOrganisationAdmin;
  const isFaterAdmin = userContext.isFaterAdmin;
  const canonicalOrgDefaultCollaborationMode = orgDetails?.defaultCollaborationMode ?? 'ASSISTED';
  const canonicalOrgFavoriteLanguage = orgDetails?.favoriteLanguage ?? 'ENGLISH';
  const [pendingOrgSettings, setPendingOrgSettings] = useState<{
    mode: CollaborationMode;
    language: 'ENGLISH' | 'FRENCH' | null;
  } | null>(null);
  const orgDefaultCollaborationMode = pendingOrgSettings?.mode ?? canonicalOrgDefaultCollaborationMode;
  const orgFavoriteLanguage = pendingOrgSettings?.language ?? canonicalOrgFavoriteLanguage;
  const [isSavingOrgSettings, setIsSavingOrgSettings] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isAgentPlaybookModalOpen, setIsAgentPlaybookModalOpen] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [isInvitesLoading, setIsInvitesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // For Add Member modal
  const [isCreating, setIsCreating] = useState(false);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoTimestamp, setLogoTimestamp] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCreateCustomPlanModalOpen, setIsCreateCustomPlanModalOpen] = useState(false);
  const [initialEmailForModal, setInitialEmailForModal] = useState<string | undefined>(undefined);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const currentUserAuthId = user?.id ?? null;
  const currentUserEmails = useMemo(() => {
      if (!user) return [];
      const emails = (user.emailAddresses || [])
          .map(addr => addr.emailAddress?.toLowerCase())
          .filter((email): email is string => !!email);
      const primary = user.primaryEmailAddress?.emailAddress?.toLowerCase();
      if (primary && !emails.includes(primary)) {
          emails.unshift(primary);
      }
      return emails;
  }, [user]);

  const impersonatedEmail = useMemo(() => {
      if (!userContext?.isImpersonating) return null;
      const email = userContext.impersonatedUser?.email;
      return email ? email.toLowerCase() : null;
  }, [userContext]);

  const viewerIsOrgAdminOfCurrentOrg = Boolean(
    isOrganisationAdmin &&
    userContext.organisationId &&
    currentOrgId &&
    userContext.organisationId === currentOrgId
  );
  const viewerCanManageSubordinates = Boolean(isFaterAdmin || viewerIsOrgAdminOfCurrentOrg);

  const isMemberCurrentUser = useCallback((member: MemberData) => {
      if (impersonatedEmail && member.email.toLowerCase() === impersonatedEmail) {
          return true;
      }
      if (currentUserAuthId && member.authId && member.authId === currentUserAuthId) {
          return true;
      }
      if (currentUserEmails.length > 0) {
          const memberEmail = member.email.toLowerCase();
          if (currentUserEmails.includes(memberEmail)) {
              return true;
          }
      }
      return false;
  }, [currentUserAuthId, currentUserEmails, impersonatedEmail]);

  useEffect(() => {
    if (!isActive) return;
    const intervalId = setInterval(() => {
        setCurrentTime(new Date());
    }, 15000);
    return () => clearInterval(intervalId);
  }, [isActive]);

  // --- Fetch Pending Invites ---
  const fetchPendingInvites = useCallback(async (orgId: string) => {
      if (!orgId || !isOrganisationAdmin) return; // Only org admins can see invites for their org
      setIsInvitesLoading(true);
      try {
          setPendingInvites(await kernel.current?.fetchOrganisationPendingInvites(orgId) ?? []);
      } catch (err: any) {
          // Use toast for user-facing errors
          console.error(`Failed to load pending invites: ${err.message}`);
          setPendingInvites([]);
      } finally {
          setIsInvitesLoading(false);
      }
  }, [isOrganisationAdmin, kernel]);


  // --- Main Data Fetching Effect ---
  useEffect(() => {
    if (!isActive) return;

    const fetchData = async () => {
        if(!kernel.current) return;
        const currentOrgId = targetOrgId || kernel.current.getUserContext().organisationId;
        const existing = currentOrgId ? kernel.current.getInventory().Organisation.registry[currentOrgId] : null;
        
        // Only show the blocking loader if we have absolutely no data for this org.
        // If data exists but is stale, we refresh silently in the background.
        if (!existing) {
            setIsLoading(true);
        }
        
        setError(null);
        setLogoExists(true);
        setPendingInvites([]);

        try {
            await kernel.current?.refreshOrganisationDetails(targetOrgId, 'full');

             const updatedOrgId = targetOrgId || kernel.current.getUserContext().organisationId;
             if (shouldLoadPendingInvites && updatedOrgId && (isOrganisationAdmin || isFaterAdmin)) {
                 await fetchPendingInvites(updatedOrgId);
             }

        } catch (err: any) {
            setError(err.message || 'An unknown error occurred');
            console.error(`Error loading organisation data: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
      };

      fetchData();
  }, [isActive, targetOrgId, isOrganisationAdmin, isFaterAdmin, shouldLoadPendingInvites, fetchPendingInvites, kernel]);

  const membersWithStatus = useMemo(() => {
    return members
      .map(member => {
        let calculatedStatus: { type: 'live' | 'recent' | 'offline'; text: string; color: string } = {
          type: 'offline', text: 'Offline', color: 'text-white/25'
        };
        
        if (member.lastActive) {
          const minutesAgo = Math.floor((currentTime.getTime() - new Date(member.lastActive).getTime()) / (60 * 1000));
          
          if (minutesAgo < 2) {
            calculatedStatus = { type: 'live', text: 'Live', color: 'text-[#4DAB9A]' };
          } else if (minutesAgo < 60) {
            calculatedStatus = { type: 'recent', text: `${minutesAgo}m ago`, color: 'text-[#FFDC49]' };
          }
        }
        return { ...member, status: calculatedStatus };
      })
      .sort((a, b) => {
        // RULE 1: Users with avatars first
        // We check imageUrl (which reflects the customAvatarUrl from the DB)
        const aHasAvatar = !!a.imageUrl;
        const bHasAvatar = !!b.imageUrl;

        if (aHasAvatar !== bHasAvatar) {
          return aHasAvatar ? -1 : 1; // a comes first if it has an avatar and b doesn't
        }

        // RULE 2: Order by generation count (Most to Least)
        const aGens = a.generationCount ?? 0;
        const bGens = b.generationCount ?? 0;
        
        return bGens - aGens; // Descending order
      });
  }, [members, currentTime]);

  
  const handleSavePlaybook = useCallback((newPlaybook: string) => {
    const currentOrgId = targetOrgId || userContext.organisationId;
    if (!currentOrgId) return;
    void kernel.current?.syncEntity('Organisation', currentOrgId);
  }, [targetOrgId, userContext.organisationId, kernel]);

  const handleSaveOrganisationSettings = useCallback(async (
    nextMode: CollaborationMode,
    nextLanguage: 'ENGLISH' | 'FRENCH' | null,
  ) => {
    const currentOrg = targetOrgId || orgDetails?.id;
    if (!currentOrg) return;
    setIsSavingOrgSettings(true);
    const toastId = toast.loading('Saving organisation settings...');
    try {
      await kernel.current?.saveOrganisationSettings(
        currentOrg,
        { defaultCollaborationMode: nextMode, favoriteLanguage: nextLanguage },
        targetOrgId,
      );
      toast.success('Organisation settings updated.', { id: toastId });
    } catch (err: any) {
      toast.error(`Failed to update organisation settings: ${err?.message || 'Unknown error'}`, { id: toastId });
    } finally {
      setPendingOrgSettings(null);
      setIsSavingOrgSettings(false);
    }
  }, [kernel, orgDetails?.id, targetOrgId]);

  const handleOrgDefaultCollaborationModeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMode = event.target.value as CollaborationMode;
    setPendingOrgSettings({ mode: nextMode, language: orgFavoriteLanguage });
    void handleSaveOrganisationSettings(nextMode, orgFavoriteLanguage);
  }, [handleSaveOrganisationSettings, orgFavoriteLanguage]);

  const handleOrgFavoriteLanguageChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLanguage = event.target.value as 'ENGLISH' | 'FRENCH';
    setPendingOrgSettings({ mode: orgDefaultCollaborationMode, language: nextLanguage });
    void handleSaveOrganisationSettings(orgDefaultCollaborationMode, nextLanguage);
  }, [handleSaveOrganisationSettings, orgDefaultCollaborationMode]);

  const handleOpenCreateDirectlyModal = useCallback((email: string) => {
    setInitialEmailForModal(email);
    setIsAddMemberModalOpen(true);
  }, []);

  const handleOpenAddMemberModal = useCallback(() => {
    setIsAddMemberModalOpen(true);
  }, []);

  const handleCloseAddMemberModal = useCallback(() => {
    setIsAddMemberModalOpen(false);
    setInitialEmailForModal(undefined);
  }, []);

  const handleInvoiceLinkChanged = useCallback(() => {
    if (currentOrgId) void kernel.current?.syncEntity('Organisation', currentOrgId);
  }, [currentOrgId, kernel]);

  const handleOpenCreateCustomPlanModal = useCallback(() => {
    setIsCreateCustomPlanModalOpen(true);
  }, []);

  const handleCloseCreateCustomPlanModal = useCallback(() => {
    setIsCreateCustomPlanModalOpen(false);
  }, []);

  const handleOpenAgentPlaybookModal = useCallback(() => {
    setIsAgentPlaybookModalOpen(true);
  }, []);

  const handleCloseAgentPlaybookModal = useCallback(() => {
    setIsAgentPlaybookModalOpen(false);
  }, []);

  const handleLogoError = useCallback(() => {
    setLogoExists(false);
  }, []);


  // --- Handlers ---
  const handleAddMemberSubmit = useCallback(async (formData: { email: string; }) => {
      const currentOrgId = targetOrgId || orgDetails?.id; // Use targetOrgId if provided (Fater Admin), else use fetched orgId
      if (!currentOrgId) {
        console.error("Organisation ID not available.");
        return;
      }
      setIsSubmitting(true);
      try {
          const result = await kernel.current?.inviteOrganisationMember(currentOrgId, formData.email);
          if (!result) { throw new Error("Kernel is not ready."); }
          toast.success(result.message || `Invitation sent to ${formData.email}.`);
          setIsAddMemberModalOpen(false);
          // Re-fetch invites after sending a new one
          await fetchPendingInvites(currentOrgId);
      } catch (error: any) {
          console.error(`Failed to send invite: ${error.message}`);
      } finally {
          setIsSubmitting(false);
      }
  }, [targetOrgId, orgDetails?.id, kernel, fetchPendingInvites]);

  const handleCreateMemberSubmit = useCallback(async (formData: { email: string; firstName: string; lastName: string; }): Promise<string> => {
      const currentOrgId = targetOrgId || orgDetails?.id;
      if (!currentOrgId) {
        toast.error("Organisation ID not available.");
        throw new Error("Organisation ID not available.");
      }
      setIsCreating(true);
      try {
          const result = await kernel.current?.createOrganisationMemberDirectly(currentOrgId, formData, targetOrgId);
          if (!result) { throw new Error("Kernel is not ready."); }
          toast.success(result.message || `User ${formData.email} created.`);
          return result.password; // Return password to modal
      } catch (error: any) {
          toast.error(`Failed to create user: ${error.message}`);
          throw error; // re-throw for modal to handle
      } finally {
          setIsCreating(false);
      }
  }, [targetOrgId, orgDetails?.id, kernel]);

  const handleResendInvite = useCallback(async (email: string) => {
      const currentOrgId = targetOrgId || orgDetails?.id;
      if (!currentOrgId) {
        console.error("Organisation ID not available.");
        return;
      }
      const toastId = toast.loading(`Resending invite to ${email}...`);
      try {
          const result = await kernel.current?.resendOrganisationInvite(currentOrgId, email);
          if (!result) { throw new Error("Kernel is not ready."); }
          toast.success(result.message || `Invitation resent to ${email}.`, { id: toastId });
          // Optionally re-fetch invites, though status might not change immediately
          await fetchPendingInvites(currentOrgId);
      } catch (error: any) {
          console.error(`Failed to resend invite: ${error.message}`, { id: toastId });
      }
  }, [targetOrgId, orgDetails?.id, kernel, fetchPendingInvites]);

  const handleDeleteInvite = useCallback(async (inviteId: string) => {
    const toastId = toast.loading(`Deleting invitation...`);
    try {
      const result = await kernel.current?.deleteOrganisationInvite(inviteId);
      if (!result) { throw new Error("Kernel is not ready."); }
      toast.success(result.message || `Invitation deleted.`, { id: toastId });
      setPendingInvites(prevInvites => prevInvites.filter(invite => invite.id !== inviteId));
    } catch (error: any) {
      toast.error(`Failed to delete invite: ${error.message}`, { id: toastId });
    }
  }, [kernel]);

  const handleLogoAreaClick = useCallback(() => {
      if (viewerCanManageSubordinates && !isLogoUploading && fileInputRef.current) {
          fileInputRef.current.click();
      }
  }, [viewerCanManageSubordinates, isLogoUploading]);

  const handleLogoFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
      const currentOrgId = targetOrgId || orgDetails?.id;
      if (!event.target.files || event.target.files.length === 0) return;
      if (!currentOrgId) { console.error("Organisation details not loaded."); return; }

      const file = event.target.files[0];
      if(!file) return;

      setIsLogoUploading(true);
      const toastId = toast.loading('Uploading logo...');
      try {
          await kernel.current?.uploadOrganisationLogo(currentOrgId, file);
          toast.success('Logo uploaded successfully!', { id: toastId });
          setLogoExists(true);
          setLogoTimestamp(Date.now()); // Force image reload
      } catch (error: any) {
          // Le retour d'erreur reste la console : sans ça le toast de chargement, jamais résolu,
          // resterait affiché indéfiniment (y compris quand la validation du fichier a échoué).
          toast.dismiss(toastId);
          console.error(`Logo upload failed: ${error.message}`, { id: toastId });
      } finally {
          setIsLogoUploading(false);
          if (fileInputRef.current) { fileInputRef.current.value = ''; }
      }
  }, [targetOrgId, orgDetails?.id, kernel]);

  const handleMemberAvatarMutation = useCallback(() => {
    setLogoTimestamp(Date.now());
  }, []);

  const handleCreateCustomPlanSubmit = useCallback(async (planDetails: CreatePlanDetails): Promise<void> => {
    // Only Fater Admins viewing a target org can do this
    if (!isFaterAdmin || !targetOrgId) {
        console.error("Permission denied to create custom plan.");
        throw new Error("Permission denied.");
    }
    try {
        const response = await axios.post('/api/admin', {
            action: 'createCustomPlan',
            ...planDetails // Spread the rest of the details
        });
        toast.success(`Custom plan "${response.data.name}" created successfully!`);
        // Potentially refresh plan data here if needed
    } catch (err) {
        let message = "An unexpected error occurred.";
        if (err instanceof AxiosError && err.response?.data) {
            message = typeof err.response.data === 'string'
                ? err.response.data
                : (err.response.data.message || JSON.stringify(err.response.data));
        } else if (err instanceof Error) {
            message = err.message;
        }
        console.error(`Failed to create custom plan: ${message}`);
        throw new Error(message); // Re-throw for modal handling
    }
  }, [isFaterAdmin, targetOrgId]);

  const {
    avatarTimestamp: memberAvatarTimestamp,
    buildMemberContextActions,
    memberActionModals,
  } = useOrganisationMemberActions({
    organisationId: currentOrgId,
    refreshOrganisationId: targetOrgId,
    members: membersWithStatus,
    isFaterAdmin,
    canDelete: isOrganisationAdmin || isFaterAdmin,
    canManageSubordinates: viewerCanManageSubordinates,
    isCurrentUser: isMemberCurrentUser,
    onAvatarMutation: handleMemberAvatarMutation,
  });
  const combinedAvatarTimestamp = Math.max(logoTimestamp, memberAvatarTimestamp);

  // --- Calculations ---
  const getPlanRemainingCredits = useCallback((plan: (typeof activePlans)[number]) => {
    const included = plan.includedCredits ?? 0;
    const used = plan.usedCredits ?? 0;
    const reserved = plan.reservedCredits ?? 0;
    const teamRemaining = plan.teamRemainingCredits ?? 0;
    const orgRemaining = Math.max(0, included - used - reserved);
    return orgRemaining + teamRemaining;
  }, []);

  const totalRemainingCredits = useMemo(() => {
    return activePlans.reduce((sum, plan) => sum + getPlanRemainingCredits(plan), 0);
  }, [activePlans, getPlanRemainingCredits]);

  const orgTeams = useMemo(() => orgDetails?.teams ?? [], [orgDetails?.teams]);

  const refreshTeamManagementData = useCallback(async () => {
    await kernel.current?.refreshOrganisationDetails(targetOrgId, 'full');
  }, [kernel, targetOrgId]);

  // Determine if the current viewer is an admin of the org being viewed
  const viewerIsAdminOfCurrentOrg = useMemo(() => {
      if (isFaterAdmin) return true;
      if (!isOrganisationAdmin) return false;
      
      // If target is specified, it must match the user's own organisation
      if (targetOrgId) {
          return targetOrgId === userContext.organisationId;
      }
      
      // If no target specified, we are viewing our own org (default context)
      return true;
  }, [isFaterAdmin, isOrganisationAdmin, targetOrgId, userContext.organisationId]);

  // --- Render Logic ---
  if (isActive && isLoading) {
      return <div className="flex h-64 items-center justify-center p-6"><SimpleLoader message="Loading organisation data…" /></div>;
  }

  if (isActive && error) {
    return <div className="rounded-lg border border-[#FF7369]/20 bg-[#FF7369]/10 p-4 text-center text-[13px] text-[#FF7369]">Could not load this organisation: {error}</div>;
  }

  if (isActive && !isLoading && !orgDetails) {
      return <div className="p-6 text-center text-[13px] text-white/45">Organisation data unavailable.</div>;
  }

  if (!isActive || !orgDetails) {
     return null; // Don't render anything if not active or no details
  }

  const logoSrc = `/api/organisation/logo?orgId=${orgDetails.id}&t=${logoTimestamp}`;
  const canChangeLogo = viewerIsAdminOfCurrentOrg || (isFaterAdmin && targetOrgId); // Allow Fater admin to change logo too
  const showGeneralSettings = settingsView === 'all' || settingsView === 'general';
  const showPeopleSettings = settingsView === 'all' || settingsView === 'people';
  const showUsageSettings = settingsView === 'all' || settingsView === 'usage';
  const showBillingSettings = settingsView === 'all' || settingsView === 'billing';
  const isSettingsEmbed = settingsView !== 'all';
  const visiblePlans = activePlans
    .filter((plan) => {
      const remaining = getPlanRemainingCredits(plan);
      return remaining > 0 && (!plan.expirationDate || new Date(plan.expirationDate) > new Date());
    })
    .sort((left, right) => {
      if (!left.expirationDate) return 1;
      if (!right.expirationDate) return -1;
      return new Date(left.expirationDate).getTime() - new Date(right.expirationDate).getTime();
    });

  return (
    <div className={isSettingsEmbed ? 'overflow-visible text-white' : 'mx-auto w-full max-w-[920px] overflow-y-auto px-[clamp(20px,3vmin,40px)] py-[clamp(20px,3vmin,36px)] text-white'}>
        {showGeneralSettings && (
          <OrganisationSection title="Profile">
            <OrganisationSettingRow
              label="Logo"
              description={orgDetails.name}
              control={(
                <button
                  type="button"
                  className="group relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[12px] border border-[#262626] bg-[#1B1B1B] text-white/40 transition-colors hover:bg-white/[0.07] hover:text-white/75 disabled:cursor-default"
                  onClick={handleLogoAreaClick}
                  title={canChangeLogo ? 'Change organisation logo' : orgDetails.name}
                  aria-label={canChangeLogo ? 'Change organisation logo' : 'Organisation logo'}
                  disabled={!canChangeLogo || isLogoUploading}
                >
                  {logoExists ? (
                    <img
                      key={logoSrc}
                      src={logoSrc}
                      alt="Organisation logo"
                      className="h-full w-full object-contain p-1.5"
                      onError={handleLogoError}
                    />
                  ) : (
                    <ImageIcon size={19} />
                  )}
                  {canChangeLogo && !isLogoUploading ? (
                    <span className="absolute inset-0 flex items-center justify-center bg-[#222222]/95 opacity-0 transition-opacity group-hover:opacity-100">
                      <Upload size={15} />
                    </span>
                  ) : null}
                  {isLogoUploading ? (
                    <span className="absolute inset-0 flex items-center justify-center bg-[#222222]/95">
                      <SimpleLoader size={18} />
                    </span>
                  ) : null}
                </button>
              )}
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleLogoFileChange}
              accept="image/png, image/jpeg, image/gif, image/svg+xml, image/webp"
              className="hidden"
              disabled={isLogoUploading}
            />
          </OrganisationSection>
        )}

        {showUsageSettings && viewerIsAdminOfCurrentOrg ? (
          <OrganisationSection title="Credits">
            <OrganisationSettingRow
              label={orgDetails.allowOverage ? 'Overage' : 'Remaining credits'}
              description={orgDetails.allowOverage ? 'Usage can continue beyond the included balance.' : 'Available across active organisation plans.'}
              control={(
                <span className="tabular-nums text-[#ECECEC]" style={{ fontSize: 15 }}>
                  {orgDetails.allowOverage
                    ? `Enabled${orgDetails.overageDisplayText ? ` · ${orgDetails.overageDisplayText}` : ''}`
                    : formatCreditsInteger(totalRemainingCredits)}
                </span>
              )}
            />
            {!orgDetails.allowOverage ? visiblePlans.map((plan, index) => {
              const metaParts = [
                plan.expirationDate ? `Expires ${format(new Date(plan.expirationDate), 'MMM d, yyyy')}` : null,
                plan.priceDisplayText,
              ].filter(Boolean).join(' · ');
              return (
                <OrganisationSettingRow
                  key={plan.id || index}
                  label={plan.name || `Credit plan ${index + 1}`}
                  description={metaParts || 'Active plan'}
                  control={<span className="tabular-nums text-[#AFAFAF]" style={{ fontSize: 15 }}>{formatCreditsInteger(getPlanRemainingCredits(plan))} credits</span>}
                />
              );
            }) : null}
          </OrganisationSection>
        ) : null}

        {showUsageSettings && viewerIsAdminOfCurrentOrg && orgDetails && (
          <section className="mb-10">
            <OrganisationConsumptionTimeline 
              organisationId={orgDetails.id} 
              organisationName={orgDetails.name}
            />
          </section>
        )}

        {showUsageSettings ? (
          <OrganisationSection title="All time">
            {stats ? (
              <>
                <OrganisationSettingRow label="Members" control={<span className="tabular-nums text-[#ECECEC]" style={{ fontSize: 15 }}>{stats.totalMembers.toLocaleString()}</span>} />
                <OrganisationSettingRow label="Projects" control={<span className="tabular-nums text-[#ECECEC]" style={{ fontSize: 15 }}>{stats.totalProjects.toLocaleString()}</span>} />
                <OrganisationSettingRow label="Generations" control={<span className="tabular-nums text-[#ECECEC]" style={{ fontSize: 15 }}>{stats.totalGenerationsAllTime.toLocaleString()}</span>} />
              </>
            ) : (
              <p className="py-4 text-[#8E8E8E]" style={{ fontSize: 14 }}>Usage statistics are not available.</p>
            )}
          </OrganisationSection>
        ) : null}

        {showGeneralSettings && (viewerIsAdminOfCurrentOrg || (isFaterAdmin && targetOrgId)) && (
          <OrganisationSection
            title="Defaults"
            action={isSavingOrgSettings ? <span className="text-[11px] text-white/35">Saving…</span> : undefined}
          >
            <OrganisationSettingRow
              label="Default collaboration mode"
              description="Controls how new sessions balance execution and human approval."
              control={(
                <select
                  id="org-default-collaboration-mode"
                  value={orgDefaultCollaborationMode}
                  onChange={handleOrgDefaultCollaborationModeChange}
                  disabled={isSavingOrgSettings}
                  className={selectClass}
                  style={CONTROL_TEXT}
                >
                  <option value="ASSISTED">Assisted</option>
                  <option value="COLLABORATIVE">Collaborative</option>
                  <option value="AUTONOMOUS">Autonomous</option>
                </select>
              )}
            />
            <OrganisationSettingRow
              label="Interface language"
              description="Used as the default when a member has no personal language preference."
              control={(
                <select
                  id="org-favorite-language"
                  value={orgFavoriteLanguage ?? 'ENGLISH'}
                  onChange={handleOrgFavoriteLanguageChange}
                  disabled={isSavingOrgSettings}
                  className={selectClass}
                  style={CONTROL_TEXT}
                >
                  <option value="ENGLISH">English</option>
                  <option value="FRENCH">French</option>
                </select>
              )}
            />
          </OrganisationSection>
        )}

        {showGeneralSettings ? (
          <OrganisationSection title="Agent playbook">
            <OrganisationSettingRow
              label="Instructions every agent reads"
              description={orgDetails.agentPlaybook && orgDetails.agentPlaybook.trim() !== ''
                ? <span className="line-clamp-3 whitespace-pre-wrap">{orgDetails.agentPlaybook}</span>
                : 'No playbook has been set.'}
              control={viewerIsAdminOfCurrentOrg ? (
                <button type="button" onClick={handleOpenAgentPlaybookModal} className={secondaryButtonClass} style={CONTROL_TEXT}>Edit</button>
              ) : <span className="text-[#8E8E8E]" style={{ fontSize: 14 }}>Read only</span>}
              align="start"
            />
          </OrganisationSection>
        ) : null}

        {showPeopleSettings && (
          <TeamManagementSection
            organisationId={orgDetails.id}
            teams={orgTeams}
            members={membersWithStatus}
            avatarTimestamp={combinedAvatarTimestamp}
            isViewerAdmin={viewerIsAdminOfCurrentOrg || (isFaterAdmin && !!targetOrgId)}
            currentUserId={userContext.dbUserId}
            onAddMember={handleOpenAddMemberModal}
            onTeamsChanged={refreshTeamManagementData}
            buildMemberContextActions={buildMemberContextActions}
            canOpenContextMenuFor={isMemberCurrentUser}
          />
        )}

        {showPeopleSettings && viewerIsAdminOfCurrentOrg && (pendingInvites.length > 0 || isInvitesLoading) && (
          <OrganisationSection title="Pending invitations">
            {isInvitesLoading ? (
              <div className="flex items-center justify-center p-4 text-[13px] text-white/40"><SimpleLoader message="Loading invitations…" size={24} /></div>
            ) : (
              <PendingInvitesList invites={pendingInvites} onResend={handleResendInvite} onDelete={handleDeleteInvite} onCreateDirectly={handleOpenCreateDirectlyModal} />
            )}
          </OrganisationSection>
        )}

        {showBillingSettings && (isFaterAdmin && targetOrgId) && (
          <OrganisationSection
            title="Custom plans"
            action={(
              <button type="button" onClick={handleOpenCreateCustomPlanModal} className={primaryButtonClass} style={CONTROL_TEXT}>
                <PackagePlus size={14} /> Add custom plan
              </button>
            )}
          >
            <p className="py-3 text-[#8E8E8E]" style={{ fontSize: 14 }}>Create a credit plan for this organisation.</p>
          </OrganisationSection>
        )}

        {showBillingSettings && isFaterAdmin && currentOrgId && (
          <OrganisationSection title="Invoices">
            <InvoicesSection
              organisationId={currentOrgId}
              plans={activePlans}
              onPlansChanged={handleInvoiceLinkChanged}
            />
          </OrganisationSection>
        )}

        {/* Modals */}
        {/* Show Add Member modal if viewer is admin for the current context */}
        {(viewerIsAdminOfCurrentOrg || (isFaterAdmin && targetOrgId)) && (
          <AddMemberModal
             isOpen={isAddMemberModalOpen} onClose={handleCloseAddMemberModal}
             onSubmit={handleAddMemberSubmit} isLoading={isSubmitting}
             onCreateDirect={handleCreateMemberSubmit} isCreating={isCreating}
             initialEmail={initialEmailForModal}
          />
        )}
        {/* Show Create Custom Plan modal only for Fater Admins viewing a target org */}
        {isFaterAdmin && targetOrgId && orgDetails && (
           <CreateCustomPlanModal
             isOpen={isCreateCustomPlanModalOpen}
             onClose={handleCloseCreateCustomPlanModal}
             onSubmit={handleCreateCustomPlanSubmit}
             targetOrgId={orgDetails.id}
             targetOrgName={orgDetails.name}
           />
        )}

        {viewerIsAdminOfCurrentOrg && (
            <AgentPlaybookModal
                isOpen={isAgentPlaybookModalOpen}
                onClose={handleCloseAgentPlaybookModal}
                organisationId={orgDetails.id}
                onSave={handleSavePlaybook}
            />
        )}

        {memberActionModals}
    </div>
  );
};

export default OrganisationMenu;
